import { CheckBadgeIcon, DocumentArrowDownIcon } from "@heroicons/react/24/solid";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { A } from "@/atoms-react/a/A.react";
import Loader from "@/atoms-react/loader/Loader.react";
import { Base } from "@/atoms-react/text/Text.react";
import { useDocument } from "@/features/user/document/hooks/use-document";
import type { Document } from "@/features/user/document/types";
import { usei18n } from "@/features/utils/hooks/use-i18n";

// const { leUuid } = useCompanyContext();
//   const { companyDetailQuery } = useCompany();
// const { checkPermission } = useUserCheckAccess();
//   const { data: companyDetails } = companyDetailQuery(leUuid as string);
//   const enabled = (() =>
//     !leUuid
//       ? true
//       : !companyDetails?.legalEntity.UserValidation &&
//         checkPermission(MandateService.ALIASES_MANAGEMENT, null, companyDetails))();

export type UploadFileProps = {
  realIdentityUuid: string;
  uploadedFile: string;
  documentsType: number;
  enabled?: boolean;
  setResponse?: (doc: Document) => void;
  dataCy?: string;
} & React.ComponentPropsWithoutRef<"div">;

export const UploadFileV2 = ({
  realIdentityUuid,
  className,
  setResponse = () => {},
  uploadedFile,
  documentsType,
  enabled = true,
  dataCy,
  ...props
}: UploadFileProps) => {
  // Traduction
  const { getLanguage } = usei18n();
  const { t } = useTranslation(["pass"]);

  // States / Refs
  const htmlInputRef = useRef<HTMLInputElement | null>(null);
  const [uploaded, setUploaded] = useState<string>(uploadedFile);
  const [loading, setLoading] = useState<boolean>(false);

  // Requests Documents
  const { DocumentsDetailsQuery, UploadDocumentMutation } = useDocument();
  const { data: documents, refetch: refetchDocuments } = DocumentsDetailsQuery(realIdentityUuid);
  const { mutateAsync: uploadDocument } = UploadDocumentMutation();
  //

  useEffect(() => {
    if (documents) {
      const doc = documents.find((doc) => doc.UUID === uploadedFile);
      if (doc) {
        setResponse(doc);
        setUploaded(doc.UUID);
      }
    }
  }, []);

  // Events
  const onDropEvent = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();

    if (!event?.dataTransfer?.files) return;
    onUpload(event.dataTransfer.files);
  };

  const onUpload = async (files: FileList | null) => {
    if (!files || loading) return;
    setLoading(true);

    const toBase64 = (file: File) =>
      new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve((reader.result as string).split(",")[1]);
        reader.onerror = reject;
      });

    const documentCountBeforeUpload = documents?.length || 0;
    const documentCountBeforeUploadFiles = documents;

    const size = files[0].size / 1024 / 1024;
    if (size > 5) {
      setLoading(false);
      toast.error(t("aliasDocumentsPage.error_import_large"));
      return;
    }

    const base64 = await toBase64(files[0]);
    try {
      await uploadDocument({
        ownerUUID: realIdentityUuid,
        language: getLanguage(),
        document_type_id: documentsType,

        document: {
          base64Doc: base64,
          fileFormat: files[0].type,
          fileName: files[0].name
        }
      });

      const { data: newDocuments } = await refetchDocuments();

      if ((newDocuments?.length || 0) > documentCountBeforeUpload) {
        const newDoc = newDocuments?.filter(
          (objet) => !documentCountBeforeUploadFiles?.some((item) => item.Number === objet.Number)
        );

        setResponse(newDoc?.[0] as Document);
        setUploaded(newDoc?.[0].UUID as string);
        toast.success(t("aliasDocumentsPage.successfully_added"));
      } else {
        toast.error(t("aliasDocumentsPage.error_import"));
      }
      setLoading(false);
    } catch (_) {
      setLoading(false);
      toast.error(t("aliasDocumentsPage.error_import"));
    }
  };

  return (
    <div
      className={twMerge(
        "flex h-auto flex-col items-center justify-center rounded-sm border-2 border-slate-100 border-dashed p-6",
        "w-full rounded-sm border-2 border-slate-50 bg-slate-50 px-4 py-3 font-semibold text-black duration-300 placeholder:font-semibold placeholder:text-slate-400 hover:border-slate-100 hover:bg-slate-100 focus:border-primary focus:bg-white dark:border-slate-800 dark:bg-slate-800 dark:text-white dark:focus:border-primary dark:focus:bg-slate-900 dark:hover:border-slate-800 dark:hover:bg-slate-900 dark:placeholder:text-slate-600",
        className
      )}
      onDragOver={(event: React.DragEvent<HTMLDivElement>) => event.preventDefault()}
      onDrop={onDropEvent}
      {...props}
    >
      {loading && <Loader />}
      {!loading &&
        (!uploadedFile && !uploaded ? (
          <div>
            <DocumentArrowDownIcon className="h-12 w-12 min-w-12 text-slate-300 md:mb-3" />
            <Base className="text-center">
              <A
                disabled={!enabled}
                onClick={() => {
                  if (!htmlInputRef.current) return;
                  htmlInputRef.current.click();
                }}
              >
                {t("aliasDocumentsPage.button")}
              </A>
              <span> {t("aliasDocumentsPage.button_label")} </span>
            </Base>
            <input
              data-cy={dataCy}
              type="file"
              onChange={(event) => {
                onUpload(event.target.files);
              }}
              hidden
              ref={htmlInputRef}
              disabled={!enabled}
            />
          </div>
        ) : (
          <div>
            <input
              data-cy={dataCy}
              type="file"
              onChange={(event) => {
                onUpload(event.target.files);
              }}
              hidden
              ref={htmlInputRef}
            />
            <CheckBadgeIcon className="h-6 w-6 text-primary md:mb-3" />
            <Base className="text-center text-primary">
              <span>{t("aliasDocumentsPage.successfully_added")}</span>
              <A
                onClick={() => {
                  if (!htmlInputRef.current) return;
                  htmlInputRef.current.click();
                }}
                className="font-semibold underline"
              >
                {t("aliasDocumentsPage.button_reupload")}
              </A>
            </Base>
          </div>
        ))}
    </div>
  );
};
